/**
 * Shared button components for theme and consistency.
 * Use these instead of raw MUI Button so styling and hierarchy stay in one place.
 *
 * - PrimaryButton:          main CTA (e.g. "New service order", "Apply filters", "Save")
 * - SecondaryButton:        secondary actions (e.g. "Show filters", "Cancel")
 * - TertiaryButton:         low emphasis, text-only (e.g. links, subtle actions)
 * - DestructiveButton:      low-emphasis destructive (e.g. "Clear filters", "Remove" inline)
 * - FilledDestructiveButton: high-emphasis destructive for irreversible confirm dialogs (e.g. "Delete")
 *
 * Size prop:
 * - default (no size prop): 40px height - use in toolbars, action bars, table rows
 * - size="large": 56px height - use in forms alongside text fields, dropdowns, file selectors
 *
 * All use sentence case (no all-caps). Pass standard MUI Button props (startIcon, onClick, size, disabled, etc.).
 */
import React from 'react';
import { Button } from '@mui/material';

const getCommonSx = (size) => ({
    textTransform: 'none',
    fontWeight: 600,
    height: size === 'large' ? '56px' : '40px', // 'large' for forms (56px), default for toolbars (40px)
    minHeight: size === 'large' ? '56px' : '40px',
    paddingLeft: size === 'large' ? 3 : 2,  // 24px / 16px so label never clips
    paddingRight: size === 'large' ? 3 : 2,
    whiteSpace: 'nowrap',
    overflow: 'visible',
});

export function PrimaryButton({ children, size, ...props }) {
    return (
        <Button
            variant="contained"
            color="primary"
            {...props}
            sx={{ ...getCommonSx(size), ...props.sx }}
        >
            {children}
        </Button>
    );
}

export function SecondaryButton({ children, size, ...props }) {
    return (
        <Button
            variant="outlined"
            color="primary"
            {...props}
            sx={{ ...getCommonSx(size), ...props.sx }}
        >
            {children}
        </Button>
    );
}

export function TertiaryButton({ children, size, ...props }) {
    return (
        <Button
            variant="text"
            color="inherit"
            {...props}
            sx={{
                ...getCommonSx(size),
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                ...props.sx,
            }}
        >
            {children}
        </Button>
    );
}

export function DestructiveButton({ children, size, ...props }) {
    return (
        <Button
            variant="text"
            color="error"
            {...props}
            sx={{ ...getCommonSx(size), ...props.sx }}
        >
            {children}
        </Button>
    );
}

/** High-emphasis destructive button — use only in confirmation dialogs for irreversible actions. */
export function FilledDestructiveButton({ children, size, ...props }) {
    return (
        <Button
            variant="contained"
            color="error"
            {...props}
            sx={{ ...getCommonSx(size), ...props.sx }}
        >
            {children}
        </Button>
    );
}

export default {
    PrimaryButton,
    SecondaryButton,
    TertiaryButton,
    DestructiveButton,
    FilledDestructiveButton,
};
